<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  ArrowRight,
  ArrowUpFromLine,
  Check,
  Copy,
  LoaderCircle,
  MonitorCheck,
  RefreshCw,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-vue-next'
import { useLiveStore } from '@/stores/live'
import { copyText } from '@/utils/clipboard'
import { buildInfo } from '@/services/build-info'

const MINIMUM_VISIBLE_MS = 650
const emit = defineEmits<{ 'visibility-change': [occupied: boolean] }>()

const { t } = useI18n()
const live = useLiveStore()
const gateComplete = (status: typeof live.ocrStatus | undefined) => status === 'idle' || status === 'ready'
const captureIsConnecting = computed(() => live.captureStatus === 'selected' || live.captureStatus === 'connecting')
const waitingForOcrStart = computed(() => live.captureStatus === 'active' && live.ocrStatus === 'idle')
const capturePreparationActive = computed(() => captureIsConnecting.value || waitingForOcrStart.value)
const visible = ref(false)
const dismissed = ref(false)
let openedAt: number | null = null
let hideRequested = false
let hideTimer: number | undefined
let focusBeforeDialog: HTMLElement | null = null

const shouldShow = computed(() => visible.value && !dismissed.value)
watch(shouldShow, (showing) => {
  if (showing) emit('visibility-change', true)
}, { flush: 'sync' })
const setupIsBusy = computed(() => live.ocrStatus !== 'error'
  && (capturePreparationActive.value || !gateComplete(live.ocrStatus)))

const stageLabel = computed(() => {
  if (live.ocrStatus === 'error') return t('startup.errorStatus')
  if (live.ocrStatus === 'initializing') return t('startup.startingEngine')
  if (live.captureStatus === 'connecting') return t('startup.connectingFrame')
  if (live.captureStatus === 'selected' && live.ocrStatus === 'idle') return t('startup.windowSelectedStage')
  return t(`startup.${live.ocrStage}`)
})

const startupTitle = computed(() => live.ocrStatus === 'initializing'
  ? t('startup.initializingTitle')
  : t('startup.title'))

const startupSubtitle = computed(() => live.ocrStatus === 'initializing'
  ? t('startup.initializingSubtitle')
  : live.captureStatus === 'connecting'
    ? t('startup.connectingSubtitle')
    : t('startup.subtitle'))

const localHint = computed(() => live.ocrStatus === 'initializing'
  ? t('startup.initializingLocalHint')
  : t('startup.localHint'))

const timedOut = computed(() => live.ocrStartupDiagnostic?.outcome === 'timeout')
const errorTitle = computed(() => timedOut.value ? t('startup.timeoutTitle') : t(`startup.${live.ocrErrorType ?? 'unknown'}ErrorTitle`))
const errorHint = computed(() => timedOut.value
  ? t(live.ocrStartupDiagnostic?.stages.sdk.status === 'running' ? 'startup.sdkTimeoutHint' : 'startup.timeoutHint')
  : t(`startup.${live.ocrErrorType ?? 'unknown'}ErrorHint`))

const initializationFailed = computed(() => live.ocrStatus === 'error' && ['initialization', 'simd', 'insecure-context'].includes(live.ocrErrorType ?? ''))
const indeterminate = computed(() => live.ocrStatus === 'initializing')
const showProgress = computed(() => !initializationFailed.value)
const progressValue = computed(() => Math.min(100, Math.max(0, live.ocrProgress)))

// 三步骤状态计算
const windowStepStatus = computed<'done' | 'active' | 'waiting'>(() => {
  if (live.captureStatus === 'active') return 'done'
  if (live.captureStatus === 'connecting' || live.captureStatus === 'selected') return 'active'
  return 'waiting'
})

const modelStepStatus = computed<'done' | 'active' | 'waiting' | 'error'>(() => {
  if (live.ocrStatus === 'error' && live.ocrErrorType === 'resource') return 'error'
  if (['prepared', 'initializing', 'ready'].includes(live.ocrStatus)) return 'done'
  if (['checking', 'detection', 'recognition'].includes(live.ocrStatus)) return 'active'
  return 'waiting'
})

const engineStepStatus = computed<'done' | 'active' | 'waiting' | 'error'>(() => {
  if (live.ocrStatus === 'error' && live.ocrErrorType !== 'resource') return 'error'
  if (live.ocrStatus === 'ready') return 'done'
  if (live.ocrStatus === 'initializing') return 'active'
  return 'waiting'
})

const copying = ref(false)
const copyState = ref<'idle' | 'copied' | 'failed'>('idle')
const diagnosticText = computed(() => JSON.stringify({
  schemaVersion: 1, build: buildInfo, browser: navigator.userAgent,
  status: live.ocrStatus, errorType: live.ocrErrorType, error: live.ocrError,
  startup: live.ocrStartupDiagnostic,
}, null, 2))
let copyRevision = 0
watch(() => live.ocrStatus, () => { copyRevision += 1; copying.value = false; copyState.value = 'idle' })

async function copyDiagnostic(): Promise<void> {
  if (copying.value) return
  copying.value = true
  const revision = copyRevision
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const copied = await Promise.race([
      copyText(diagnosticText.value),
      new Promise<boolean>((resolve) => { timer = setTimeout(() => resolve(false), 3_000) }),
    ])
    if (revision === copyRevision) copyState.value = copied ? 'copied' : 'failed'
  } catch {
    if (revision === copyRevision) copyState.value = 'failed'
  } finally {
    clearTimeout(timer)
    if (revision === copyRevision) copying.value = false
  }
}

function clearHideTimer(): void {
  if (hideTimer === undefined) return
  window.clearTimeout(hideTimer)
  hideTimer = undefined
}

function show(): void {
  clearHideTimer()
  hideRequested = false
  if (visible.value) return
  const activeElement = document.activeElement
  focusBeforeDialog = activeElement instanceof HTMLElement && activeElement !== document.body
    ? activeElement
    : null
  openedAt = null
  visible.value = true
}

function hideWhenStable(): void {
  clearHideTimer()
  hideRequested = true
  if (openedAt === null) return
  const delay = Math.max(0, MINIMUM_VISIBLE_MS - (Date.now() - openedAt))
  hideTimer = window.setTimeout(() => {
    hideTimer = undefined
    visible.value = false
  }, delay)
}

function markDialogOpened(): void {
  openedAt = Date.now()
  if (hideRequested) hideWhenStable()
}

function retry(): void {
  dismissed.value = false
  hideRequested = false
  openedAt = Date.now()
  visible.value = true
  void live.initializeOcr()
}

function dismiss(): void {
  clearHideTimer()
  dismissed.value = true
  visible.value = false
}

function restoreDialogFocus(): void {
  const previous = focusBeforeDialog
  focusBeforeDialog = null
  openedAt = null
  hideRequested = false
  queueMicrotask(() => {
    const target = previous?.isConnected
      ? previous
      : document.querySelector<HTMLElement>('[data-capture-window-action]:not([disabled])')
        ?? document.querySelector<HTMLElement>('#main-content')
    target?.focus({ preventScroll: true })
    if (!shouldShow.value) emit('visibility-change', false)
  })
}

function requiresStartupDialog(
  captureStatus: typeof live.captureStatus | undefined,
  status: typeof live.ocrStatus | undefined,
): boolean {
  if (captureStatus === undefined && status === undefined) return false
  return captureStatus === 'connecting'
    || captureStatus === 'selected'
    || (captureStatus === 'active' && status === 'idle')
    || !gateComplete(status)
}

watch([() => live.captureStatus, () => live.ocrStatus], ([captureStatus, status], previous = [undefined, undefined]) => {
  const [previousCaptureStatus, previousOcrStatus] = previous
  const required = requiresStartupDialog(captureStatus, status)
  const wasRequired = requiresStartupDialog(previousCaptureStatus, previousOcrStatus)
  if (!required) {
    if (visible.value) hideWhenStable()
    return
  }
  if (!wasRequired) dismissed.value = false
  if (!dismissed.value) show()
}, { immediate: true })

onBeforeUnmount(() => {
  clearHideTimer()
  emit('visibility-change', false)
})
</script>

<template>
  <el-dialog
    :model-value="shouldShow"
    class="ocr-startup-dialog"
    :class="{ 'is-error': live.ocrStatus === 'error' }"
    modal-class="ocr-startup-overlay"
    width="28rem"
    align-center
    append-to-body
    destroy-on-close
    lock-scroll
    trap-focus
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    transition="ocr-dialog-fade"
    :aria-busy="setupIsBusy"
    @opened="markDialogOpened"
    @closed="restoreDialogFocus"
  >
    <template #header="{ titleId, titleClass }">
      <div class="startup-header">
        <div
          class="startup-status-icon"
          :class="live.ocrStatus === 'error' ? ['is-error', 'error'] : ['is-loading', 'loading']"
          aria-hidden="true"
        >
          <TriangleAlert v-if="live.ocrStatus === 'error'" />
          <LoaderCircle v-else />
        </div>
        <div class="startup-header-meta">
          <h2 :id="titleId" :class="[titleClass, 'startup-title']">
            {{ live.ocrStatus === 'error' ? errorTitle : startupTitle }}
          </h2>
          <div class="startup-subtitle-row">
            <span v-if="live.captureStatus === 'selected' || live.captureStatus === 'connecting' || live.captureStatus === 'active'" class="connection-tag">
              <MonitorCheck aria-hidden="true" />
              {{ t(live.captureStatus === 'active' ? 'startup.windowConnected' : 'startup.windowSelected') }}
            </span>
            <span class="startup-desc">{{ live.ocrStatus === 'error' ? errorHint : startupSubtitle }}</span>
          </div>
        </div>
      </div>
    </template>

    <div v-if="live.ocrStatus !== 'error'" class="startup-main-flow">
      <!-- 三阶段状态流 Stepper -->
      <div class="startup-stepper" role="list">
        <!-- 步骤 1: 画面接入 -->
        <div class="stepper-item" :class="`is-${windowStepStatus}`" role="listitem">
          <div class="step-indicator">
            <Check v-if="windowStepStatus === 'done'" class="step-icon done" aria-hidden="true" />
            <span v-else-if="windowStepStatus === 'active'" class="step-pulse" aria-hidden="true" />
            <span v-else class="step-dot" aria-hidden="true" />
          </div>
          <div class="step-content">
            <span class="step-title">{{ t('startup.stepWindow') }}</span>
            <span class="step-sub">
              {{ windowStepStatus === 'done' ? t('startup.stepWindowConnected') : t('startup.stepWindowConnecting') }}
            </span>
          </div>
        </div>

        <div class="step-connector" :class="{ 'is-active': windowStepStatus === 'done' }" aria-hidden="true" />

        <!-- 步骤 2: 模型载入 -->
        <div class="stepper-item" :class="`is-${modelStepStatus}`" role="listitem">
          <div class="step-indicator">
            <Check v-if="modelStepStatus === 'done'" class="step-icon done" aria-hidden="true" />
            <span v-else-if="modelStepStatus === 'active'" class="step-pulse" aria-hidden="true" />
            <span v-else class="step-dot" aria-hidden="true" />
          </div>
          <div class="step-content">
            <span class="step-title">{{ t('startup.stepModel') }}</span>
            <span class="step-sub">
              {{ modelStepStatus === 'done' ? t('startup.stepModelReady') : modelStepStatus === 'active' ? `${progressValue}%` : t('startup.stepModelLoading') }}
            </span>
          </div>
        </div>

        <div class="step-connector" :class="{ 'is-active': modelStepStatus === 'done' }" aria-hidden="true" />

        <!-- 步骤 3: 引擎启动 -->
        <div class="stepper-item" :class="`is-${engineStepStatus}`" role="listitem">
          <div class="step-indicator">
            <Check v-if="engineStepStatus === 'done'" class="step-icon done" aria-hidden="true" />
            <span v-else-if="engineStepStatus === 'active'" class="step-pulse" aria-hidden="true" />
            <span v-else class="step-dot" aria-hidden="true" />
          </div>
          <div class="step-content">
            <span class="step-title">{{ t('startup.stepEngine') }}</span>
            <span class="step-sub">
              {{ engineStepStatus === 'done' ? t('startup.stepEngineReady') : engineStepStatus === 'active' ? t('startup.stepEngineStarting') : t('startup.ready') }}
            </span>
          </div>
        </div>
      </div>

      <!-- 进度反馈与详细阶段 -->
      <div class="startup-progress">
        <div class="progress-status" aria-live="polite" aria-atomic="true">
          <span class="stage-label">
            <span class="status-dot" aria-hidden="true" />
            <Transition name="stage-swap" mode="out-in">
              <strong :key="`${live.ocrStatus}:${live.ocrStage}`">{{ stageLabel }}</strong>
            </Transition>
          </span>
          <output v-if="showProgress && !indeterminate">{{ progressValue }}%</output>
        </div>
        <div
          v-if="showProgress"
          class="progress-track"
          :class="{ 'is-indeterminate': indeterminate }"
          role="progressbar"
          :aria-label="t('startup.progress')"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-valuenow="indeterminate ? undefined : progressValue"
        >
          <span class="progress-value" :style="indeterminate ? undefined : { transform: `scaleX(${progressValue / 100})` }" />
        </div>
      </div>

      <!-- 极简信任徽章 -->
      <div class="local-trust-card" role="note">
        <ShieldCheck class="trust-icon" aria-hidden="true" />
        <div class="trust-copy">
          <span class="trust-title">{{ t('startup.localProcessing') }}</span>
          <small class="trust-hint">{{ localHint }}</small>
        </div>
      </div>
    </div>

    <!-- 错误恢复视图 -->
    <div v-else class="startup-error-flow">
      <div class="stage-label error-stage-label" aria-live="polite">
        <TriangleAlert aria-hidden="true" />
        <strong>{{ stageLabel }}</strong>
      </div>

      <div v-if="initializationFailed" class="startup-fixes">
        <p id="startup-fixes-label" class="startup-fixes-label">{{ t('startup.fixesLabel') }}</p>
        <ol class="startup-fix-list" aria-labelledby="startup-fixes-label">
          <li>
            <div class="startup-fix">
              <span class="startup-fix-icon is-update" aria-hidden="true"><ArrowUpFromLine /></span>
              <span class="startup-fix-copy">
                <strong>{{ t('startup.updateBrowserFixTitle') }}</strong>
                <small>{{ t('startup.updateBrowserFixHint') }}</small>
              </span>
            </div>
          </li>
          <li>
            <div class="startup-fix">
              <span class="startup-fix-icon is-browser" aria-hidden="true"><MonitorCheck /></span>
              <span class="startup-fix-copy">
                <strong>{{ t('startup.browserRecommendationTitle') }}</strong>
                <small>{{ t('startup.browserRecommendationHint') }}</small>
              </span>
            </div>
          </li>
        </ol>
      </div>
      <div v-else class="browser-recommendation" role="note">
        <span class="supporting-icon" aria-hidden="true"><MonitorCheck /></span>
        <span>
          <strong>{{ t('startup.browserRecommendationTitle') }}</strong>
          <small>{{ t('startup.browserRecommendationHint') }}</small>
        </span>
      </div>

      <div class="startup-actions">
        <el-button type="primary" @click="retry">
          <RefreshCw aria-hidden="true" />{{ t(initializationFailed ? 'startup.retryEngine' : 'startup.retry') }}
        </el-button>
        <el-button @click="dismiss">
          {{ t('startup.continue') }}<ArrowRight aria-hidden="true" />
        </el-button>
        <el-button :disabled="copying" @click="copyDiagnostic">
          <Copy aria-hidden="true" />{{ t('startup.copyDiagnostic') }}
        </el-button>
      </div>
      <div v-if="copyState !== 'idle'" class="startup-diagnostic">
        <p role="status">{{ t(copyState === 'copied' ? 'startup.diagnosticCopied' : 'startup.diagnosticCopyFailed') }}</p>
        <textarea v-if="copyState === 'failed'" readonly :aria-label="t('startup.diagnosticLabel')" :value="diagnosticText" />
      </div>
    </div>
  </el-dialog>
</template>

<style scoped>
:global(.ocr-startup-overlay.el-overlay) {
  --ocr-startup-scrim: rgb(8 12 11 / 50%);
  backdrop-filter: blur(4px);
  background: var(--ocr-startup-scrim);
}
:global(:root[data-theme='dark'] .ocr-startup-overlay.el-overlay) {
  --ocr-startup-scrim: rgb(0 0 0 / 65%);
}
:global(.ocr-startup-overlay .el-overlay-dialog) {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
:global(.ocr-startup-dialog.el-dialog) {
  width: min(28rem, calc(100vw - 1.5rem)) !important;
  max-height: calc(100dvh - 1.5rem);
  flex: none;
  margin: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  contain: layout paint;
  border: 0.0625rem solid var(--border);
  border-radius: var(--radius-card, 0.75rem);
  background: var(--surface-raised);
  box-shadow: var(--shadow-raised);
}
:global(.ocr-startup-dialog .el-dialog__header) {
  margin: 0;
  padding: 1.25rem 1.375rem 0.75rem;
  border-bottom: none;
}
:global(.ocr-startup-dialog .el-dialog__body) {
  min-height: 0;
  padding: 0 1.375rem 1.375rem;
  overflow-y: auto;
  color: var(--text);
}

/* 顶部状态 Header */
.startup-header {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}
.startup-status-icon {
  width: 2.25rem;
  height: 2.25rem;
  flex: none;
  display: grid;
  place-items: center;
  border-radius: 0.625rem;
  color: var(--blue);
  background: var(--blue-soft);
  position: relative;
  transition: all var(--motion-base) ease;
}
.startup-status-icon > svg {
  width: 1.25rem;
  height: 1.25rem;
}
.startup-status-icon.is-loading > svg {
  animation: loader-spin 1s linear infinite;
}
.startup-status-icon.is-error {
  color: var(--danger);
  background: color-mix(in srgb, var(--danger) 10%, var(--surface));
}
.startup-header-meta {
  min-width: 0;
  flex: 1;
}
.startup-title {
  margin: 0;
  color: var(--text-strong);
  font-size: 1.0625rem;
  line-height: 1.35;
  font-weight: 700;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
}
.startup-subtitle-row {
  margin-top: 0.3125rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.connection-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  color: var(--green);
  font-size: var(--font-caption, 0.75rem);
  line-height: 1.2;
  font-weight: 650;
}
.connection-tag > svg {
  width: 0.8125rem;
  height: 0.8125rem;
}
.startup-desc {
  color: var(--muted);
  font-size: var(--font-label, 0.8125rem);
  line-height: 1.45;
  overflow-wrap: anywhere;
}

/* 三阶段 Stepper 卡片容器 */
.startup-main-flow {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.startup-stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0.875rem;
  background: var(--surface-subtle);
  border: 0.0625rem solid var(--border);
  border-radius: 0.5rem;
  gap: 0.375rem;
}
.stepper-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;
}
.step-indicator {
  width: 1.25rem;
  height: 1.25rem;
  flex: none;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--surface);
  border: 0.0625rem solid var(--border);
  transition: all var(--motion-fast) ease;
}
.step-icon.done {
  width: 0.75rem;
  height: 0.75rem;
  color: #ffffff;
}
.is-done .step-indicator {
  background: var(--green);
  border-color: var(--green);
}
.is-active .step-indicator {
  border-color: var(--blue);
  background: var(--blue-soft);
}
.step-pulse {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--blue);
  animation: pulse-ring 1.8s ease-in-out infinite;
}
.step-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: var(--muted-strong);
  opacity: 0.4;
}
.step-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.step-title {
  font-size: var(--font-caption, 0.75rem);
  font-weight: 650;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.is-waiting .step-title {
  color: var(--muted);
}
.is-active .step-title {
  color: var(--blue);
  font-weight: 700;
}
.step-sub {
  font-size: 0.6875rem;
  color: var(--muted);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.is-done .step-sub {
  color: var(--green);
  font-weight: 600;
}
.step-connector {
  width: 0.875rem;
  height: 0.0625rem;
  background: var(--border);
  flex: none;
  transition: background-color var(--motion-fast) ease;
}
.step-connector.is-active {
  background: var(--green);
}

/* 进度反馈区 */
.startup-progress {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.progress-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  min-height: 1.25rem;
}
.stage-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4375rem;
  color: var(--text);
  font-size: var(--font-label, 0.8125rem);
  line-height: 1.35;
}
.status-dot {
  width: 0.4375rem;
  height: 0.4375rem;
  border-radius: 50%;
  background: var(--blue);
  animation: status-breathe 2.2s ease-in-out infinite;
}
.stage-label strong {
  font-weight: 650;
  overflow-wrap: anywhere;
}
.progress-status output {
  min-width: 2.5rem;
  color: var(--text-strong);
  font-size: var(--font-label, 0.8125rem);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.progress-track {
  height: 0.25rem;
  overflow: hidden;
  border-radius: 999px;
  background: var(--surface-subtle);
  position: relative;
}
.progress-value {
  display: block;
  width: 100%;
  height: 100%;
  transform-origin: left center;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--blue), color-mix(in srgb, var(--blue) 80%, white));
  transition: transform 220ms var(--ease-out);
}
.is-indeterminate .progress-value {
  width: 40%;
  animation: startup-progress 1.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

/* 极简本地隐私信任卡片 */
.local-trust-card {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--green) 6%, var(--surface));
  border: 0.0625rem solid color-mix(in srgb, var(--green) 16%, var(--border));
}
.trust-icon {
  width: 1rem;
  height: 1rem;
  flex: none;
  margin-top: 0.0625rem;
  color: var(--green);
}
.trust-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.trust-title {
  color: var(--text-strong);
  font-size: var(--font-caption, 0.75rem);
  font-weight: 680;
  line-height: 1.35;
}
.trust-hint {
  color: var(--muted);
  font-size: 0.6875rem;
  line-height: 1.45;
  margin-top: 0.125rem;
}

/* 错误与恢复视图 */
.startup-error-flow {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.error-stage-label {
  color: var(--danger);
  font-weight: 650;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: var(--font-label, 0.8125rem);
}
.error-stage-label > svg {
  width: 0.9375rem;
  height: 0.9375rem;
}
.startup-fixes {
  margin-top: 0.25rem;
}
.startup-fixes-label {
  margin: 0 0 0.375rem;
  color: var(--muted);
  font-size: var(--font-caption, 0.75rem);
  font-weight: 650;
}
.startup-fix-list {
  margin: 0;
  padding: 0;
  overflow: hidden;
  list-style: none;
  border: 0.0625rem solid var(--border);
  border-radius: var(--radius-card, 0.5rem);
  background: var(--surface-subtle);
}
.startup-fix-list > li + li {
  border-top: 0.0625rem solid var(--border);
}
.startup-fix {
  min-height: 2.5rem;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem;
  display: grid;
  grid-template-columns: 1.875rem minmax(0, 1fr);
  align-items: center;
  gap: 0.625rem;
  text-align: left;
}
.startup-fix-icon {
  width: 1.875rem;
  height: 1.875rem;
  display: grid;
  place-items: center;
  border-radius: 0.5rem;
  color: var(--blue);
  background: var(--blue-soft);
}
.startup-fix-icon.is-browser {
  color: var(--muted-strong);
  background: color-mix(in srgb, var(--surface) 70%, var(--surface-raised));
}
.startup-fix-icon > svg {
  width: 0.9375rem;
  height: 0.9375rem;
}
.startup-fix-copy strong {
  display: block;
  color: var(--text-strong);
  font-size: var(--font-label, 0.8125rem);
  line-height: 1.35;
  font-weight: 650;
}
.startup-fix-copy small {
  display: block;
  margin-top: 0.125rem;
  color: var(--muted);
  font-size: var(--font-caption, 0.75rem);
  line-height: 1.45;
}
.browser-recommendation {
  padding: 0.75rem;
  display: grid;
  grid-template-columns: 1.125rem minmax(0, 1fr);
  align-items: start;
  gap: 0.625rem;
  border: 0.0625rem solid var(--border);
  border-radius: var(--radius-card, 0.5rem);
  background: var(--surface-subtle);
  text-align: left;
}
.supporting-icon {
  color: var(--blue);
  margin-top: 0.125rem;
}
.supporting-icon > svg {
  width: 1.125rem;
  height: 1.125rem;
}
.browser-recommendation strong {
  display: block;
  color: var(--text-strong);
  font-size: var(--font-label, 0.8125rem);
  font-weight: 680;
}
.browser-recommendation small {
  display: block;
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: var(--font-caption, 0.75rem);
  line-height: 1.5;
}
.startup-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.25rem;
}
.startup-actions :deep(.el-button svg) {
  width: 0.875rem;
  height: 0.875rem;
}
.startup-diagnostic {
  margin-top: 0.5rem;
  font-size: var(--font-caption, 0.75rem);
}
.startup-diagnostic textarea {
  box-sizing: border-box;
  width: 100%;
  min-height: 7rem;
  padding: 0.5rem;
  border-radius: 0.375rem;
  color: var(--text);
  background: var(--surface);
  border: 0.0625rem solid var(--border);
  font-family: monospace;
  font-size: 0.75rem;
}

/* 动效动画 */
@keyframes loader-spin {
  to { transform: rotate(360deg); }
}
@keyframes pulse-ring {
  0%, 100% { transform: scale(0.9); opacity: 0.6; }
  50% { transform: scale(1.15); opacity: 1; }
}
@keyframes status-breathe {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
@keyframes startup-progress {
  from { transform: translateX(-100%); }
  to { transform: translateX(280%); }
}

:global(.ocr-dialog-fade-enter-active) { transition: opacity 180ms ease; }
:global(.ocr-dialog-fade-leave-active) { transition: opacity 130ms ease; }
:global(.ocr-dialog-fade-enter-active .ocr-startup-dialog) {
  transition: opacity 180ms ease, transform 180ms var(--ease-out);
}
:global(.ocr-dialog-fade-leave-active .ocr-startup-dialog) {
  transition: opacity 100ms ease, transform 130ms ease;
}
:global(.ocr-dialog-fade-enter-from),
:global(.ocr-dialog-fade-leave-to) { opacity: 0; }
:global(.ocr-dialog-fade-enter-from .ocr-startup-dialog) {
  opacity: 0;
  transform: translateY(0.375rem) scale(0.985);
}
:global(.ocr-dialog-fade-leave-to .ocr-startup-dialog) {
  opacity: 0;
  transform: translateY(-0.1875rem) scale(0.99);
}
.stage-swap-enter-active { transition: opacity 150ms ease, transform 170ms var(--ease-out); }
.stage-swap-leave-active { transition: opacity 90ms ease, transform 100ms ease; }
.stage-swap-enter-from { opacity: 0; transform: translateY(0.1875rem); }
.stage-swap-leave-to { opacity: 0; transform: translateY(-0.125rem); }

@media (max-width: 32rem) {
  :global(.ocr-startup-overlay .el-overlay-dialog) { padding: 0.75rem; }
  :global(.ocr-startup-dialog .el-dialog__header) { padding: 1rem 1rem 0.5rem; }
  :global(.ocr-startup-dialog .el-dialog__body) { padding: 0 1rem 1rem; }
  .startup-stepper { flex-direction: column; align-items: stretch; gap: 0.625rem; }
  .step-connector { display: none; }
  .startup-actions { grid-template-columns: 1fr; display: grid; }
  .startup-actions :deep(.el-button) { width: 100%; }
}
</style>
